import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Star, Eye, EyeOff } from "lucide-react";

const EMPTY = { texte: "", auteur: "", contexte: "", note: 5, visible: true, ordre: 0 };

export default function AdminTemoignages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/temoignages");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.texte.trim() || !form.auteur.trim()) {
      toast.error("Texte et auteur requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        note: Math.max(1, Math.min(5, parseInt(form.note, 10) || 5)),
        ordre: parseInt(form.ordre, 10) || 0,
      };
      if (editingId) {
        await api.patch(`/admin/temoignages/${editingId}`, payload);
        toast.success("Témoignage mis à jour");
      } else {
        await api.post("/admin/temoignages", payload);
        toast.success("Témoignage ajouté");
      }
      reset();
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (t) => {
    setForm({
      texte: t.texte || "",
      auteur: t.auteur || "",
      contexte: t.contexte || "",
      note: t.note || 5,
      visible: t.visible !== false,
      ordre: t.ordre || 0,
    });
    setEditingId(t.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de ${t.auteur} ?`)) return;
    try {
      await api.delete(`/admin/temoignages/${t.id}`);
      toast.success("Témoignage supprimé");
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  const toggleVisible = async (t) => {
    try {
      await api.patch(`/admin/temoignages/${t.id}`, { visible: !t.visible });
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec");
    }
  };

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-temoignages-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-eyebrow mb-3">Témoignages</p>
          <h1 className="h-display text-4xl md:text-5xl">Paroles de clients.</h1>
          <p className="text-[14px] text-muted-foreground mt-3 max-w-2xl">
            Témoignages affichés sur la page d'accueil. Ajoute-les au fil de
            l'eau, masque-les temporairement, modifie ou supprime.
          </p>
        </div>
      </div>

      {/* FORM */}
      <form onSubmit={onSubmit} className="border border-border p-6 md:p-8 mb-12 bg-muted/30" data-testid="temoignage-form">
        <p className="text-eyebrow mb-5">{editingId ? "Modifier le témoignage" : "Nouveau témoignage"}</p>
        <div className="space-y-5">
          <div>
            <label className="text-serial block mb-2">Texte du témoignage *</label>
            <textarea
              value={form.texte}
              onChange={(e) => setField("texte", e.target.value)}
              className="w-full bg-background border border-border focus:border-primary p-3 text-[14.5px] min-h-[120px] outline-none"
              placeholder="« Une cuisine signée Luigi : la justesse des proportions… »"
              data-testid="temoignage-texte"
              required
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-serial block mb-2">Auteur *</label>
              <input
                value={form.auteur}
                onChange={(e) => setField("auteur", e.target.value)}
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
                placeholder="Famille L. / Cabinet X / M. Y"
                data-testid="temoignage-auteur"
                required
              />
            </div>
            <div>
              <label className="text-serial block mb-2">Contexte</label>
              <input
                value={form.contexte || ""}
                onChange={(e) => setField("contexte", e.target.value)}
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
                placeholder="Cuisine en chêne — Nantes"
                data-testid="temoignage-contexte"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5 items-end">
            <div>
              <label className="text-serial block mb-2">Note (1 à 5)</label>
              <input
                type="number"
                min="1"
                max="5"
                value={form.note}
                onChange={(e) => setField("note", e.target.value)}
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
                data-testid="temoignage-note"
              />
            </div>
            <div>
              <label className="text-serial block mb-2">Ordre d'affichage</label>
              <input
                type="number"
                value={form.ordre}
                onChange={(e) => setField("ordre", e.target.value)}
                className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none"
                data-testid="temoignage-ordre"
              />
            </div>
            <label className="flex items-center gap-3 text-[14px] cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) => setField("visible", e.target.checked)}
                className="h-4 w-4 accent-primary"
                data-testid="temoignage-visible"
              />
              Visible sur le site
            </label>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-8">
          <button type="submit" className="btn-terra" disabled={saving} data-testid="temoignage-submit">
            {saving ? "Enregistrement…" : editingId ? "Mettre à jour" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="text-[13px] text-muted-foreground link-underline-static">
              Annuler
            </button>
          )}
        </div>
      </form>

      {/* LIST */}
      <div>
        <p className="text-eyebrow mb-5">{items.length} témoignage{items.length > 1 ? "s" : ""}</p>
        {loading ? (
          <p className="text-serial">Chargement</p>
        ) : items.length === 0 ? (
          <div className="text-muted-foreground text-[14px] border border-dashed border-border p-8 text-center">
            Aucun témoignage pour l'instant. Ajoute le premier avec le formulaire ci-dessus.
          </div>
        ) : (
          <ul className="space-y-px bg-border" data-testid="temoignages-list">
            {items.map((t) => (
              <li
                key={t.id}
                className={`bg-background p-6 md:p-7 grid md:grid-cols-12 gap-6 items-start ${t.visible ? "" : "opacity-60"}`}
                data-testid={`temoignage-row-${t.id}`}
              >
                <div className="md:col-span-1 flex items-center gap-1 text-[hsl(var(--gold))]">
                  {Array.from({ length: Math.max(1, Math.min(5, t.note || 5)) }).map((_, i) => (
                    <Star key={i} size={12} fill="currentColor" stroke="currentColor" />
                  ))}
                </div>
                <p className="md:col-span-6 font-serif italic text-[18px] leading-snug">« {t.texte} »</p>
                <div className="md:col-span-3 text-[13.5px]">
                  <p className="font-medium">{t.auteur}</p>
                  {t.contexte && (
                    <p className="text-[11.5px] tracking-[0.1em] uppercase text-muted-foreground mt-1">{t.contexte}</p>
                  )}
                </div>
                <div className="md:col-span-2 flex items-center gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => toggleVisible(t)}
                    className="text-muted-foreground hover:text-foreground"
                    title={t.visible ? "Masquer" : "Afficher"}
                    data-testid={`temoignage-toggle-${t.id}`}
                  >
                    {t.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(t)}
                    className="text-[12px] tracking-[0.18em] uppercase link-underline-static text-foreground"
                    data-testid={`temoignage-edit-${t.id}`}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(t)}
                    className="text-destructive hover:opacity-80"
                    title="Supprimer"
                    data-testid={`temoignage-delete-${t.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
